import { fetchAuthSession } from 'aws-amplify/auth';
import { DivConst } from 'constants/divConst';
import { useApiState } from 'hooks/useApiState';
import { buildSignupSignatureHtml } from '../utils/buildSignupSignatureHtml';
import type { SignupCompanyData } from '../types';

export function useCompanyRegistration(): {
  registerCompany: (signupData: SignupCompanyData) => Promise<void>;
} {
  const { postCompanyRegister } = useApiState();

  const registerCompany = async (signupData: SignupCompanyData): Promise<void> => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (!accessToken) {
      throw new Error('Cognito access token is not available.');
    }
    const response = await postCompanyRegister({
      company: {
        name: signupData.companyName,
        tel: signupData.tel,
        zipCode: signupData.zipCode,
        // SignupCompanyData.prefectureDiv は string (FormSelect の value 型)、
        // postCompanyRegister は number を要求するため parseInt 変換する。
        prefectureDiv: parseInt(signupData.prefectureDiv, 10),
        city: signupData.city,
        address: signupData.address,
        // buildingName は string | null。FormStateType 上は string で空文字許容のため
        // 空文字を null へ正規化する（API 型は `string | null`、旧コードは any でバイパスしていた箇所）。
        buildingName: signupData.buildingName || null,
        imageUploadKey: null,
        reportMailSenderName: signupData.companyName,
        reportMailReplyAddress: 'no-reply@branq.jp',
        reportMailSignatureHtml: buildSignupSignatureHtml(signupData),
        privacyPolicyUrl: null,
        copyright: null,
        termsInputDiv: DivConst.items.termsInputType.url.value,
        termsUrl: null,
        termsText: null,
        apiKeyBenchmark: null,
        apiKeyGoogleAnalytics: null,
      },
      user: {
        accessToken,
        name: signupData.fullName,
        loginRouteDiv: DivConst.items.loginRoute.mail.value,
      },
    });
    if (response.error?.duplicated) {
      const error = new Error('同じ会社は登録できません。');
      error.name = 'CompanyNameAndTelDuplicated';
      throw error;
    }
  };

  return { registerCompany };
}
